import {
  ColDef,
  ICellRendererParams,
  ValueGetterParams,
} from "ag-grid-community";
import { CenteredGreedy, Row } from "components/Layout";
import { PrimaryContactIcon } from "components/Icons/PrimaryContactIcon";
import React from "react";
import {
  ActionButton,
  ActionButtonContainer,
} from "components/AgGrid/ActionButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBullseye,
  faPencil,
  faTrash,
  faPlus,
} from "@fortawesome/pro-solid-svg-icons";
import { ColumnType } from "components/AgGrid/constants";
import { naturalSortStringComparator } from "lib/agGrid/columns/comparators";
import { useSelector } from "react-redux";
import { AlternativeType } from "constants/businesses";
import { getDeploymentById, getSaleyards } from "selectors";
import { EmailRecipientIconList } from "components/Icons/EmailRecipientIconList";
import AgriNousActiveLogo from "img/AgriNousActiveLogo";

export const UserTypes = {
  EMAIL: "EMAIL",
  USER: "USER",
} as const;
export type UserType = (typeof UserTypes)[keyof typeof UserTypes];

// This is somewhat a munge of EmailRecipient and BusinessBusinessUser
export interface TableRowData {
  id: number;
  email: string;
  firstName: string;
  isPrimary?: boolean;
  lastName: string;
  phoneNumber: string;
  userType: UserType;
  saleYards?: string[];
  source?: {
    type: string;
    id: string;
  };
  managedBy?: {
    type: string;
    id: string;
  };
  [key: string]: any;
}

function EmailRecipientIconListRenderer(
  props: ICellRendererParams<TableRowData>,
) {
  const { data } = props;
  return (
    <Row alignCenter>
      <EmailRecipientIconList recipient={data} />
    </Row>
  );
}

function ManagedByRenderer(props: ICellRendererParams<TableRowData>) {
  const { data } = props;
  const { source, userType } = data;
  const sourceName = useSelector(state => {
    if (userType === UserTypes.EMAIL) {
      if (source.type === AlternativeType.Deployment) {
        return getDeploymentById(source.id)(state)?.name || "";
      }
      if (source.type === AlternativeType.Saleyard) {
        return getSaleyards(state)[source.id]?.name || "";
      }
    }
    return null;
  });

  if (data.userType === UserTypes.USER) {
    return "AgriNous";
  }
  return sourceName;
}

const SaleYardsRenderer = (props: ICellRendererParams<TableRowData>) => {
  const { data } = props;
  return Array.isArray(data.saleYards) && data.saleYards.length > 0
    ? data.saleYards.join(", ")
    : "-";
};
const IsPrimaryCellRenderer = (props: ICellRendererParams<TableRowData>) => {
  const { data } = props;
  const isPrimaryContact = data.isPrimary || false;
  if (isPrimaryContact) {
    return (
      <CenteredGreedy>
        <PrimaryContactIcon color="primary" title="Primary Contact" />
      </CenteredGreedy>
    );
  }
  return null;
};

const IsMynousActiveCellRenderer = (
  props: ICellRendererParams<TableRowData>,
) => {
  const { data } = props;
  const isMyNousActive = data.isMyNousActive || false;
  return (
    <Row alignCenter justifyCenter>
      <AgriNousActiveLogo isActive={isMyNousActive} scale={0.4} size={20} />
    </Row>
  );
};

function ContactActionsCellRenderer(props: ICellRendererParams<TableRowData>) {
  const { data, node, context } = props;

  const {
    onSetPrimaryContactEmail,
    onEditEmailRecipientIndexRef,
    onRemoveEmailRecipientIndexRef,
  } = context;

  const onClickPrimaryContactEmail = () => {
    onSetPrimaryContactEmail(data.email);
  };
  const onClickEditEmailRecipient = () => {
    onEditEmailRecipientIndexRef.current(data.id);
  };
  const onClickRemoveEmailRecipient = () => {
    onRemoveEmailRecipientIndexRef.current(data.id);
  };

  if (node.group) {
    return null;
  }

  return (
    <ActionButtonContainer>
      <ActionButton type="button" onClick={onClickEditEmailRecipient}>
        <FontAwesomeIcon icon={faPencil} />
        &nbsp; Edit
      </ActionButton>{" "}
      <ActionButton
        type="button"
        data-tour="delete"
        onClick={onClickRemoveEmailRecipient}
      >
        <FontAwesomeIcon icon={faTrash} />
        &nbsp; Delete
      </ActionButton>{" "}
      <ActionButton
        disabled={data.isPrimary}
        type="button"
        onClick={onClickPrimaryContactEmail}
      >
        <FontAwesomeIcon icon={faBullseye} />
        &nbsp; Set Primary
      </ActionButton>
    </ActionButtonContainer>
  );
}

function OtherActionsCellRenderer(props: ICellRendererParams<TableRowData>) {
  const { data, node, context } = props;

  const { onCopyRecipient } = context;

  const onClickCopyRecipient = () => {
    onCopyRecipient(data);
  };

  if (node.group) {
    return null;
  }

  return (
    <ActionButtonContainer>
      <ActionButton type="button" onClick={onClickCopyRecipient}>
        <FontAwesomeIcon icon={faPlus} />
        &nbsp; Add
      </ActionButton>{" "}
    </ActionButtonContainer>
  );
}

const Columns = {
  IS_PRIMARY: "c3e8efe4-c988-49fc-9952-d5f64502691b",
  EMAIL: "f6f824f8-12ba-4bd4-a148-e7fc3b0892fb",
  FIRST_NAME: "3a936733-8f3d-4a12-98a4-4aeba9dc68e4",
  LAST_NAME: "d218a87e-1604-4b9c-b6ae-38f4bded8465",
  PHONE_NUMBER: "54d2afbb-74b1-42ec-945a-8e7f8ab9bdf1",
  RECIPIENT_FOR: "84401b0c-6a41-4bb2-97cd-0f5f9b441586",
  SALE_YARDS: "9344d3c6-56c5-4da9-995b-fcd671b7bea3",
  MANAGED_BY: "a47cc5cd-fd3d-4694-8d2a-1463f4df5306",
  CONTACT_ACTIONS: "ada48c3c-03a5-4a12-91d9-1c4b6487697e",
  OTHER_ACTIONS: "1982fc3b-5c97-4087-8882-9d7aa40678ed",
  MYNOUS_ACTIVE: "d944c098-4b18-4064-abf1-dff6d75a7ed1",
} as const;

const columnDefinitions: Record<string, ColDef<TableRowData>> = {
  [Columns.IS_PRIMARY]: {
    colId: Columns.IS_PRIMARY,
    field: "isPrimary",
    headerName: "Primary",
    width: 100,
    type: ColumnType.BOOLEAN,
    cellRenderer: IsPrimaryCellRenderer,
  },
  [Columns.MYNOUS_ACTIVE]: {
    colId: Columns.MYNOUS_ACTIVE,
    field: "isMyNousActive",
    headerName: "Portal",
    width: 100,
    type: ColumnType.BOOLEAN,
    cellRenderer: IsMynousActiveCellRenderer,
  },
  [Columns.EMAIL]: {
    colId: Columns.EMAIL,
    field: "email",
    headerName: "Email",
    width: 130,
    type: ColumnType.STRING,
    comparator: naturalSortStringComparator,
  },
  [Columns.FIRST_NAME]: {
    colId: Columns.FIRST_NAME,
    field: "firstName",
    headerName: "First Name",
    width: 120,
    type: ColumnType.STRING,
    comparator: naturalSortStringComparator,
  },
  [Columns.LAST_NAME]: {
    colId: Columns.LAST_NAME,
    field: "lastName",
    headerName: "Last Name",
    width: 120,
    type: ColumnType.STRING,
    comparator: naturalSortStringComparator,
  },
  [Columns.PHONE_NUMBER]: {
    colId: Columns.PHONE_NUMBER,
    field: "phoneNumber",
    headerName: "Phone Number",
    width: 120,
    type: ColumnType.STRING,
    comparator: naturalSortStringComparator,
  },
  [Columns.RECIPIENT_FOR]: {
    colId: Columns.RECIPIENT_FOR,
    field: "recipientFor",
    headerName: "Recipient For",
    width: 150,
    type: ColumnType.ARRAY,
    comparator: naturalSortStringComparator,
    valueGetter: (params: ValueGetterParams<TableRowData>) => [
      params.data.isAccountsRecipient,
      params.data.isCommercialReportRecipient,
      params.data.isComplianceReportRecipient,
    ],
    cellRenderer: EmailRecipientIconListRenderer,
  },
  [Columns.SALE_YARDS]: {
    colId: Columns.SALE_YARDS,
    field: "saleYards",
    headerName: "Sale Yards",
    width: 130,
    type: ColumnType.ARRAY,
    cellRenderer: SaleYardsRenderer,
  },
  [Columns.MANAGED_BY]: {
    colId: Columns.MANAGED_BY,
    field: "managedBy",
    headerName: "Managed By",
    width: 100,
    type: ColumnType.STRING,
    comparator: naturalSortStringComparator,
    cellRenderer: ManagedByRenderer,
  },
  [Columns.CONTACT_ACTIONS]: {
    colId: Columns.CONTACT_ACTIONS,
    // Pin to this so we re-render when it changes.
    field: "isPrimary",
    headerName: "Actions",
    width: 230,
    cellRenderer: ContactActionsCellRenderer,
    pinned: "right",
  },
  [Columns.OTHER_ACTIONS]: {
    colId: Columns.OTHER_ACTIONS,
    field: "id",
    headerName: "Actions",
    width: 160,
    cellRenderer: OtherActionsCellRenderer,
    pinned: "right",
  },
};
export const ContactColumnDefinitions: ColDef<TableRowData>[] = [
  columnDefinitions[Columns.IS_PRIMARY],
  columnDefinitions[Columns.MYNOUS_ACTIVE],
  columnDefinitions[Columns.FIRST_NAME],
  columnDefinitions[Columns.LAST_NAME],
  columnDefinitions[Columns.EMAIL],
  columnDefinitions[Columns.PHONE_NUMBER],
  columnDefinitions[Columns.RECIPIENT_FOR],
  columnDefinitions[Columns.CONTACT_ACTIONS],
];
export const OtherContactColumnDefinitions: ColDef<TableRowData>[] = [
  columnDefinitions[Columns.MYNOUS_ACTIVE],
  columnDefinitions[Columns.MANAGED_BY],
  columnDefinitions[Columns.FIRST_NAME],
  columnDefinitions[Columns.LAST_NAME],
  columnDefinitions[Columns.EMAIL],
  columnDefinitions[Columns.PHONE_NUMBER],
  columnDefinitions[Columns.RECIPIENT_FOR],
  columnDefinitions[Columns.SALE_YARDS],
  columnDefinitions[Columns.OTHER_ACTIONS],
];
